import { getFirebaseAuth } from "@/config/firebase/firebaseClient";
import Cookies from "js-cookie";

const TOKEN_COOKIE_NAME = "firebaseToken";

const parseJsonResponse = async (response: Response) => {
  const text = await response.text();
  if (!text) {
    return null;
  }

  return JSON.parse(text);
};

const getErrorMessage = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (!value || typeof value !== "object") {
    return String(value ?? "");
  }

  const obj = value as Record<string, unknown>;

  if (typeof obj.message === "string" && obj.message) {
    return obj.message;
  }

  if (typeof obj.error === "string" && obj.error) {
    return obj.error;
  }

  if (obj.error && typeof obj.error === "object") {
    const nested = getErrorMessage(obj.error);
    if (nested) {
      return nested;
    }
  }

  try {
    return JSON.stringify(obj);
  } catch {
    return "Unknown error";
  }
};

const normalizeApiErrorMessage = (message: string) => {
  const trimmedMessage = message.trim();
  if (!trimmedMessage) {
    return "Request failed";
  }

  if (trimmedMessage.startsWith("{") || trimmedMessage.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmedMessage) as Record<string, unknown>;
      const title = typeof parsed.title === "string" ? parsed.title.trim() : "";
      const detail = typeof parsed.detail === "string" ? parsed.detail.trim() : "";
      const status = typeof parsed.status === "number"
        ? parsed.status
        : (typeof parsed.status === "string" ? Number(parsed.status) : NaN);

      if (detail) {
        return detail;
      }

      if (title && Number.isFinite(status)) {
        return `${title} (${status})`;
      }

      if (title) {
        return title;
      }
    } catch {
      // Continue with non-JSON normalization when parsing fails.
    }
  }

  if (/^<!doctype html>/i.test(trimmedMessage) || /^<html[\s>]/i.test(trimmedMessage)) {
    return "Unexpected HTML response from API route. This usually means the route is missing or returned a non-JSON error page.";
  }

  return trimmedMessage.replace(/^[^:]+ failed:\s*/i, "").trim();
};

const createApiError = (payload: unknown, fallbackMessage: string) => {
  const message = payload === undefined
    ? fallbackMessage
    : getErrorMessage(payload) || fallbackMessage;
  const error = new Error(normalizeApiErrorMessage(message)) as Error &
    Record<string, unknown>;

  if (payload && typeof payload === "object") {
    Object.assign(error, payload as Record<string, unknown>);
  }

  return error;
};

/**
 * Fetch data from an API with Firebase ID token authentication.
 * Uses lazy loading to avoid server-side Firebase imports.
 * @param url - The API endpoint to fetch.
 * @param method - The HTTP method (default is "GET").
 * @param body - The request body (optional).
 * @returns The JSON response from the API.
 * @throws An error if the request fails.
 */
export async function fetchWithAuth(
  url: string,
  method: string = "GET",
  body?: any,
  isUserOptional: boolean = false,
  isFormData: boolean = false,
  returnRawResponse: boolean = false
) {
  let user: any = null;
  let auth: Awaited<ReturnType<typeof getFirebaseAuth>> | null = null;

  try {
    auth = await getFirebaseAuth();
    if (typeof auth.authStateReady === "function") await auth.authStateReady();
    user = auth.currentUser;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`Failed to initialize Firebase Auth: ${errorMessage}`);
    if (!isUserOptional) {
      throw new Error("Failed to initialize Firebase Auth");
    }
  }

  if (!user) {
    Cookies.remove(TOKEN_COOKIE_NAME);
    if (!isUserOptional) throw new Error("User not authenticated. Please log in to continue.");
  }

  let sessionChanged = false;
  const unsubscribe = auth?.onAuthStateChanged(current => { if (current !== user) sessionChanged = true; });
  const assertCurrent = () => {
    if (sessionChanged || (auth?.currentUser ?? null) !== user) throw new Error("Authentication session changed. Please retry.");
  };
  try {
    const headers: Record<string, string> = {};
    if (user) {
      headers.Authorization = `Bearer ${await user.getIdToken()}`;
      assertCurrent();
    }
    if (!isFormData) headers["Content-Type"] = "application/json";
    const options: RequestInit = { method, headers, cache: "no-store" };

    if (body) options.body = isFormData ? body : JSON.stringify(body);
    assertCurrent();
    let response = await fetch(url, options);
    assertCurrent();
    if (response.status === 401 && user && ["GET", "HEAD"].includes(method.toUpperCase())) {
      headers.Authorization = `Bearer ${await user.getIdToken(true)}`;
      assertCurrent();
      response = await fetch(url, options);
      assertCurrent();
    }
    if (!response.ok) {
      let parsedError: unknown;
      let errorDetail = response.statusText;
      try {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const errorJson = await parseJsonResponse(response);
          parsedError = errorJson?.error && typeof errorJson.error === "object"
            ? errorJson.error
            : errorJson;
          errorDetail = getErrorMessage(parsedError) || errorDetail;
        } else {
          const errorText = await response.text();
          if (errorText) {
            errorDetail = errorText;
          }
        }
      } catch {
        errorDetail = response.statusText;
      }

      assertCurrent();
      const error = createApiError(parsedError ?? errorDetail, errorDetail);
      error.status = response.status;
      throw error;
    }

    if (returnRawResponse) return response;

    const result = await parseJsonResponse(response);
    assertCurrent();
    return result;
  } finally {
    unsubscribe?.();
  }
}
